import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { ActionTypeOrganizationAdditionalInfoConfig } from '@/api';
import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { InputMultiSelect } from '@/design_system/InputMultiSelect/InputMultiSelect';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Title from '@/design_system/Typography/Title';
import {
  useActionTypesOrganization,
  useCreateActionType,
  useUpdateActionType,
} from '@/models/actionType';
import { useDefectTypesOrganization } from '@/models/defectType';
import { I18nWording, SUPPORTED_LOCALES, useDatabaseTranslation } from '@/services/i18n';
import { trimI18nWording } from '@/utils/trimI18nWording';
import useViewPort from '@/utils/useViewport';

import { BrandsMultiSelect } from '../shared/BrandsMultiSelect';
import { IsQuantifiableRadio } from '../shared/IsQuantifiableRadio';
import { NameInputs } from '../shared/NameInputs';
import { ProductAttributeValuesMultiSelects } from '../shared/ProductAttributeValuesMultiSelects';

import { AdditionalInfoConfigInputs } from './shared/AdditionalInfoConfigInputs';
import { RefashionIdSelect } from './shared/RefashionIdSelect';
import { hasAdditionalInfoConfigError } from './utils/hasAdditionalInfoConfigError';
import { hasNameError } from './utils/hasNameError';
import { ActionDefectsInputs } from './ActionDefectsInputs';
import type { ActionDefectValue, ActionType } from './ActionsTable';
import { SparePartTypesInputs, SparePartTypesInputsValue } from './SparePartTypesInputs';

export const ActionDrawer = ({
  actionType,
  isOpen,
  onClose,
}: {
  actionType: ActionType | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Drawer isOpen={isOpen} onOpenChange={onClose}>
      <ActionDrawerContent actionType={actionType} onClose={onClose} />
    </Drawer>
  );
};

const ActionDrawerContent = ({
  actionType,
  onClose,
}: {
  actionType: ActionType | null;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const showToast = useShowToast();
  const { _db } = useDatabaseTranslation();

  const { mutateAsync: updateActionType, isPending: isUpdateActionTypePending } =
    useUpdateActionType();
  const { mutateAsync: createActionType, isPending: isCreateActionTypePending } =
    useCreateActionType();

  const [name, setName] = useState(
    Object.fromEntries(
      SUPPORTED_LOCALES.map(({ id }) => [id, actionType?.name[id] ?? ''])
    ) as I18nWording
  );
  const [isQuantifiable, setIsQuantifiable] = useState(actionType?.isQuantifiable ?? true);
  const [productAttributeValues, setProductAttributeValues] = useState(
    actionType?.productAttributeValues ?? []
  );
  const [brandIds, setBrandIds] = useState(actionType?.brands.map((brand) => brand.id) ?? []);
  const [defects, setDefects] = useState<ActionDefectValue[]>(
    actionType?.actionTypeToDefectTypes.map(
      ({ id, defectTypeOrganization, mandatory, brandIds }) => ({
        id,
        defectTypeId: defectTypeOrganization.id,
        mandatory,
        brandIds,
      })
    ) ?? []
  );
  const [children, setChildren] = useState(actionType?.children.map(({ id }) => id) ?? []);
  const [refashionId, setRefashionId] = useState(actionType?.refashionId ?? null);
  const [additionalInfoConfig, setAdditionalInfoConfig] =
    useState<ActionTypeOrganizationAdditionalInfoConfig>(actionType?.additionalInfoConfig || []);
  const [sparePartTypes, setSparePartTypes] = useState<SparePartTypesInputsValue>(
    actionType?.sparePartTypes.map(({ sparePartTypeOrganizationId, defaultQuantity }) => ({
      key: sparePartTypeOrganizationId,
      id: sparePartTypeOrganizationId,
      defaultQuantity,
    })) ?? []
  );

  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const nameError = hasNameError(name);
  const additionalInfoConfigError = hasAdditionalInfoConfigError(additionalInfoConfig);
  const sparePartTypesError = sparePartTypes.some((sp) => !sp.id);

  const hasError = nameError || additionalInfoConfigError || sparePartTypesError;

  const onSave = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      const body = {
        name: trimI18nWording(name),
        isQuantifiable,
        productAttributeValues: productAttributeValues.map(({ id }) => id),
        defects: defects.map((defect) => ({
          id: defect.defectTypeId,
          mandatory: defect.mandatory,
          brandIds: defect.brandIds,
        })),
        children,
        refashionId,
        additionalInfoConfig,
        sparePartTypes: sparePartTypes.map(({ id, defaultQuantity }) => ({
          sparePartTypeOrganizationId: id!,
          defaultQuantity,
        })),
        brandIds,
      };

      if (actionType) {
        await updateActionType({
          actionTypeId: actionType.id,
          ...body,
        });
      } else {
        await createActionType({
          ...body,
          agreementPrices: [],
        });
      }

      onClose();

      const actionName = _db(name);

      showToast({
        text: (
          <Trans id="settings.actions.drawer.success">
            Action &quot;{actionName}&quot; successfully saved
          </Trans>
        ),
      });
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const { data: defectOptions = [] } = useDefectTypesOrganization();
  const { data: actionsAndPacks } = useActionTypesOrganization();
  const actionOptions = actionsAndPacks?.actionTypes ?? [];

  return (
    <>
      <DrawerHeader>
        <Title size="300">
          {actionType ? (
            <Trans id="settings.actions.drawer.title">Update action</Trans>
          ) : (
            <Trans id="settings.actions.drawer.title-create">Create action</Trans>
          )}
        </Title>
      </DrawerHeader>

      <DrawerBody>
        <Stack padding={isMobile ? '1rem' : '2rem'} gap="1rem">
          <NameInputs name={name} setName={setName} showErrors={showErrors} />

          <IsQuantifiableRadio
            isQuantifiable={isQuantifiable}
            setIsQuantifiable={setIsQuantifiable}
          />

          <ProductAttributeValuesMultiSelects
            productAttributeValues={productAttributeValues}
            setProductAttributeValues={setProductAttributeValues}
          />

          <ActionDefectsInputs
            defects={defects}
            setDefects={setDefects}
            defectOptions={defectOptions}
            showErrors={showErrors}
          />

          <InputMultiSelect
            variant="select"
            label={<Trans id="settings.actions.drawer.child-action">Child actions</Trans>}
            placeholder={t({
              id: 'settings.actions.drawer.child-action',
              message: 'Child actions',
            })}
            value={actionOptions.filter((option) => children.includes(option.id))}
            options={actionOptions.filter((option) => option.id !== actionType?.id)}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => _db(option.name)}
            onChange={(options) => setChildren(options.map((option) => option.id))}
            onRemoveValue={(optionId) => setChildren(children.filter((id) => id !== optionId))}
            isDisabled={!actionOptions.length}
          />

          <SparePartTypesInputs
            sparePartTypes={sparePartTypes}
            setSparePartTypes={setSparePartTypes}
            showErrors={showErrors}
          />

          <BrandsMultiSelect selectedBrandIds={brandIds} setBrandIds={setBrandIds} />

          <RefashionIdSelect refashionId={refashionId} setRefashionId={setRefashionId} />

          <AdditionalInfoConfigInputs
            additionalInfoConfig={additionalInfoConfig}
            setAdditionalInfoConfig={setAdditionalInfoConfig}
            showErrors={showErrors}
          />
        </Stack>
      </DrawerBody>

      <Footer>
        {(apiError || (showErrors && hasError)) && (
          <FooterError>
            {!apiError && showErrors && hasError ? (
              <Trans id="settings.actions.drawer.error">Please fill the missing information</Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}

        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onClose}>
            <Trans id="settings.actions.drawer.cancel">Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            size="medium"
            onPress={onSave}
            isLoading={isUpdateActionTypePending || isCreateActionTypePending}
          >
            <Trans id="settings.actions.drawer.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
